import { useRef, useState } from 'react'
import type { ExerciseOpen as ExerciseOpenType } from '../types'
import { useTranslation } from 'react-i18next'
import { useExerciseSlot } from '../hooks/useExerciseSet'

interface ExerciseOpenProps {
  exercise: ExerciseOpenType
  /** Position dans la série d'exercices du message — voir `ExerciseSetProvider`. */
  slotIndex: number
}

const NO_ANSWER = '—'

export function ExerciseOpen({ exercise, slotIndex }: Readonly<ExerciseOpenProps>) {
  const { t } = useTranslation()
  const [value, setValue] = useState('')
  const [showHint, setShowHint] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const hasAnswer = value.trim().length > 0

  const { locked } = useExerciseSlot(slotIndex, hasAnswer, () => {
    const trimmed = value.trim()
    return {
      itemType: 'open',
      question: exercise.question,
      answerLabel: trimmed ? `"${trimmed}"` : `"${NO_ANSWER}"`,
      correct: null,
      answered: hasAnswer,
    }
  })

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value)
    // auto-resize
    const el = e.target
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-[var(--color-neutral-900)]">{exercise.question}</p>

      {exercise.hint && (
        <div>
          <button
            type="button"
            onClick={() => setShowHint((v) => !v)}
            className="flex items-center gap-1.5 text-xs font-medium text-[var(--color-primary)] hover:underline min-h-[44px] py-1"
          >
            <span aria-hidden="true">💡</span>
            <span>{showHint ? t('session.exercise_hide_hint') : t('session.exercise_show_hint')}</span>
          </button>
          {showHint && (
            <div className="mt-1 rounded-lg px-3 py-2 text-xs bg-[var(--color-primary-light)] text-[var(--color-neutral-700)] border-l-2 border-[var(--color-primary)]">
              {exercise.hint}
            </div>
          )}
        </div>
      )}

      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleInput}
        disabled={locked}
        placeholder={t('session.open_answer_placeholder')}
        rows={3}
        className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 resize-none overflow-hidden focus:outline-none focus:border-[var(--color-primary)] bg-white disabled:bg-gray-50 disabled:text-gray-400 transition-colors min-h-[80px]"
      />

      {locked && (
        <p className="text-xs text-gray-400">{t('session.exercise_pending_feedback')}</p>
      )}
    </div>
  )
}
